// src/components/social/Notifications.jsx
import React, { useContext, useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { FaBell, FaCheck, FaSpinner } from 'react-icons/fa';
import { NotificationContext } from '../../context/notificationContext';
import NotificationItem from './NotificationItem';

const Notifications = () => {
  const [isOpen, setIsOpen] = useState(false);
  const { 
    notifications, 
    unreadCount, 
    loading, 
    fetchNotifications, 
    markAllAsRead 
  } = useContext(NotificationContext);
  
  const notificationsRef = useRef(null);

  // Fetch notifications on mount
  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        notificationsRef.current && 
        !notificationsRef.current.contains(event.target)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  // Toggle notifications dropdown
  const toggleNotifications = () => {
    setIsOpen(prev => !prev);
  };

  // Handle marking all as read
  const handleMarkAllAsRead = (e) => {
    e.stopPropagation();
    markAllAsRead();
  };

  return (
    <div className="relative" ref={notificationsRef}>
      <button 
        className="relative p-2 text-gray-700 hover:text-primary transition-colors rounded-full hover:bg-gray-100"
        onClick={toggleNotifications}
        aria-label="Notifications"
      >
        <FaBell size={18} />
        {unreadCount > 0 && (
          <span className="absolute top-0 right-0 -mt-1 -mr-1 flex items-center justify-center w-5 h-5 text-xs font-bold text-white bg-red rounded-full">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>
      
      {isOpen && (
        <div className="absolute right-0 mt-2 w-80 sm:w-96 bg-white rounded-lg shadow-lg overflow-hidden z-20">
          <div className="px-4 py-3 border-b border-gray-200 flex justify-between items-center">
            <h3 className="font-medium text-gray-900">Notifications</h3>
            {unreadCount > 0 && (
              <button 
                className="text-xs flex items-center text-primary hover:text-indigo-700 transition-colors"
                onClick={handleMarkAllAsRead}
              >
                <FaCheck className="mr-1" size={12} /> Mark all as read
              </button>
            )}
          </div>
          
          <div className="max-h-96 overflow-y-auto">
            {loading ? (
              <div className="flex justify-center items-center py-8">
                <FaSpinner className="animate-spin text-primary" size={24} />
              </div>
            ) : notifications.length === 0 ? (
              <div className="py-8 text-center text-gray-500">
                <div className="mb-2 flex justify-center">
                  <FaBell className="text-gray-300" size={32} />
                </div>
                <p>No notifications yet</p>
              </div>
            ) : (
              <div className="divide-y divide-gray-100">
                {notifications.slice(0, 5).map(notification => (
                  <NotificationItem 
                    key={notification._id} 
                    notification={notification} 
                  />
                ))}
              </div>
            )}
          </div>
          
          <div className="px-4 py-3 bg-gray-50 border-t border-gray-200 text-center">
            <Link 
              to="/notifications" 
              className="text-sm text-primary hover:text-indigo-700 font-medium"
              onClick={() => setIsOpen(false)}
            >
              View all notifications
            </Link>
          </div>
        </div>
      )}
    </div>
  );
};

export default Notifications;